import type {
  ContactDetailsInfoDuplicatesProps,
  DuplicateContact,
} from './index.types';
import { Icon } from 'Components/shared/Icon';
import { ChevronRight } from 'lucide-react';
import { MobXProviderContext, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~/lib/ui/collapsible';
import { cn } from '~/lib/utils';
import { ContactDuplicateGroup } from '../../../../ContactDuplicateGroup';

/**
 * ContactDetailsInfoDuplicates is a component that displays the duplicate contacts in the sidebar.
 */
export const ContactDetailsInfoDuplicates = observer(
  ({
    phoneNumbers,
    contact,
    openContactDetails,
  }: ContactDetailsInfoDuplicatesProps) => {
    const { person: personStore } = useContext(
      MobXProviderContext
    ) as RootStoreProps;

    const [isOpen, setIsOpen] = useState(false);

    // Trigger loading of contacts when phone numbers change
    useEffect(() => {
      if (!phoneNumbers || !contact) {
        return;
      }

      phoneNumbers.forEach((phoneNumber) => {
        void personStore.getAllContactsWithSameNum(phoneNumber.number);
      });
    }, [contact, personStore, phoneNumbers]);

    // Get duplicates reactively from MobX store
    const duplicates: DuplicateContact[] = [];

    if (phoneNumbers && contact) {
      phoneNumbers.forEach((phoneNumber) => {
        const contactsPbo = personStore.getAllContactsWithSameNum(
          phoneNumber.number
        );

        if (contactsPbo?.state === 'fulfilled') {
          const contacts = contactsPbo.value.filter((c) => c.id !== contact.id);

          if (contacts.length > 0) {
            duplicates.push({
              phoneNumber: phoneNumber.number,
              contacts,
            });
          }
        }
      });
    }

    if (!duplicates.length) {
      return null;
    }

    const totalDuplicates = duplicates.flatMap((d) => d.contacts).length;
    const multipleDuplicates = totalDuplicates > 1;

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);

          if (open) {
            pushToGTMDataLayer('contact_details_expand_duplicates', {
              duplicates: totalDuplicates,
            });
          }
        }}
      >
        <CollapsibleTrigger className="flex items-center gap-1 mb-3 px-2 py-1 hover:cursor-pointer">
          <ChevronRight
            className={cn('size-6 text-muted-foreground transition-transform', {
              'rotate-90': isOpen,
            })}
          />
          {totalDuplicates} Duplicate{multipleDuplicates ? 's' : ''} found
          <div
            title={`Found ${totalDuplicates} other contact${
              multipleDuplicates ? 's' : ''
            } with the same phone number`}
          >
            <Icon icon="warning-orange" size="medium" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {duplicates.map((duplicate) => (
            <ContactDuplicateGroup
              key={duplicate.phoneNumber}
              phoneNumber={duplicate.phoneNumber}
              contacts={duplicate.contacts}
              onNavigateToContact={openContactDetails}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  }
);
